import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  Package, 
  TrendingUp, 
  TrendingDown, 
  FileText, 
  Users, 
  Building2, 
  LogOut,
  Menu,
  X,
  Scan,
  Settings,
  UserCog,
  Calendar,
  Truck,
  ChevronDown
} from 'lucide-react';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState(['inventario', 'operaciones']);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Menú organizado por secciones
  const menuSections = [
    {
      id: 'principal',
      label: null, // Sin título, siempre visible
      items: [
        { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['super_admin', 'admin', 'user'] },
        { path: '/scanner', icon: Scan, label: 'Escáner Móvil', roles: ['super_admin', 'admin', 'operator', 'user'] },
      ]
    },
    {
      id: 'inventario',
      label: 'Inventario',
      roles: ['super_admin', 'admin', 'user', 'operator'],
      items: [
        { path: '/products', icon: Package, label: 'Productos', roles: ['super_admin', 'admin', 'user'] },
        { path: '/entries', icon: TrendingUp, label: 'Entradas', roles: ['super_admin', 'admin', 'operator', 'user'] },
        { path: '/exits', icon: TrendingDown, label: 'Salidas', roles: ['super_admin', 'admin', 'operator', 'user'] },
      ]
    },
    {
      id: 'operaciones',
      label: 'Operaciones',
      roles: ['super_admin', 'admin', 'user', 'operator'],
      items: [
        { path: '/customers', icon: Calendar, label: 'Eventos', roles: ['super_admin', 'admin', 'user', 'operator'] },
        { path: '/delivery-notes', icon: FileText, label: 'Albaranes', roles: ['super_admin', 'admin', 'user'] },
        { path: '/suppliers', icon: Truck, label: 'Proveedores', roles: ['super_admin', 'admin', 'user'] },
      ]
    },
    {
      id: 'configuracion',
      label: 'Configuración',
      roles: ['super_admin', 'admin', 'user'],
      items: [
        { path: '/company-settings', icon: Settings, label: 'Mi Empresa', roles: ['super_admin', 'admin', 'user'] },
        { path: '/operators', icon: UserCog, label: 'Operadores', roles: ['admin', 'user'] },
        { path: '/users', icon: Users, label: 'Usuarios', roles: ['admin'] },
        { path: '/companies', icon: Building2, label: 'Empresas', roles: ['super_admin'] },
      ]
    }
  ];

  // Filtrar secciones y items por rol
  const getFilteredSections = () => {
    return menuSections
      .filter(section => !section.roles || section.roles.includes(user?.role))
      .map(section => ({
        ...section,
        items: section.items.filter(item => item.roles.includes(user?.role))
      }))
      .filter(section => section.items.length > 0);
  };

  const filteredSections = getFilteredSections();

  const getRoleBadge = () => {
    const roleLabels = {
      'admin': { label: 'Administrador', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
      'user': { label: 'Usuario', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
      'operator': { label: 'Operador', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
      'super_admin': { label: 'Super Admin', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' }
    };
    const role = roleLabels[user?.role] || { label: user?.role, color: 'bg-slate-100 text-slate-700' };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${role.color}`}>
        {role.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 z-[60] flex items-center justify-between px-4 shadow-sm">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="InventarioPlus" className="h-10 w-10 object-contain" />
          <span className="font-bold text-slate-900 dark:text-white">InventarioPlus</span>
        </div>
        <Button
          variant="ghost"
          className="h-10 w-10 p-0"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full w-72 lg:w-64 bg-white dark:bg-slate-900
          border-r border-slate-200 dark:border-slate-800
          transition-transform duration-300 ease-in-out z-50
          lg:translate-x-0 shadow-xl lg:shadow-none
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3 mb-3">
            <img src="/logo.png" alt="InventarioPlus" className="h-12 w-12 object-contain" />
            <div>
              <h1 className="text-lg font-bold text-slate-900 dark:text-white">InventarioPlus</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">Management Solutions</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center">
              <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.name}</p>
              {getRoleBadge()}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto" style={{ height: 'calc(100vh - 200px)' }}>
          {filteredSections.map((section) => (
            <div key={section.id} className="mb-2">
              {section.label && (
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hover:text-slate-700 dark:hover:text-slate-300"
                >
                  {section.label}
                  <ChevronDown className={`h-4 w-4 transition-transform ${expandedSections.includes(section.id) ? 'rotate-0' : '-rotate-90'}`} />
                </button>
              )}
              
              {(!section.label || expandedSections.includes(section.id)) && (
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path || 
                      (item.path === '/customers' && location.pathname.startsWith('/events'));
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <div 
                          className={`
                            flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150
                            ${isActive 
                              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30' 
                              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }
                          `}
                        >
                          <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400 dark:text-slate-500'}`} />
                          <span className="font-medium text-sm">{item.label}</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-600 dark:text-slate-300 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 h-11 rounded-lg"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-5 w-5 mr-3" />
            Cerrar Sesión
          </Button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0 bg-slate-50 dark:bg-slate-950">
        <div className="p-4 lg:p-6 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};
